import { useEffect, useState } from "react";
import PublicCalendar from "./PublicCalendar";
import {
	Typography,
	Button,
	Stack,
	Container,
	Checkbox,
	Box,
	Modal,
	Fade,
	Paper,
	Card,
	CardContent,
	CardActions,
	Divider,
	Accordion,
	AccordionSummary,
	AccordionDetails,
	IconButton,
	Popover,
} from "@mui/material";
import EventNoteIcon from "@mui/icons-material/EventNote";
import CalendarMonthIcon from "@mui/icons-material/CalendarViewMonth";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import SchoolIcon from "@mui/icons-material/School";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import MenuBookIcon from "@mui/icons-material/MenuBook";
import ShoppingCartIcon from "@mui/icons-material/ShoppingCart";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import { Link } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { collection, getDocs } from "firebase/firestore";
import { db } from "../firebase";

export default function Information() {
	const [isDocOpen, setIsDocOpen] = useState(false);
	const [infoAnchor, setInfoAnchor] = useState<HTMLElement | null>(null);
	const { profile, updateProfile, rosterToday, isSignedIn } = useAuth();
	const [contacts, setContacts] = useState<any[]>([]);
	const [loading, setLoading] = useState(true);

	useEffect(() => {
		const fetchContacts = async () => {
			try {
				const snapshot = await getDocs(collection(db, "contacts"));
				const contactsData = snapshot.docs.map((doc) => ({
					id: doc.id,
					...doc.data(),
				}));
				setContacts(contactsData);
				setLoading(false);
			} catch (error) {
				console.error("Error fetching contacts:", error);
				setLoading(false);
			}
		};

		fetchContacts();
	}, []);

	if (loading) {
		return <></>;
	}

	const dayOfWeek = (new Date().getDay() * 2001) % 7;
	const multiplier = 2346223;
	const startIndex = (dayOfWeek * multiplier) % contacts.length;
	const activeContacts = contacts.filter((c: any) => c.active !== false);
	const contactsToShow = [
		activeContacts[(startIndex + 0) % activeContacts.length],
		activeContacts[(startIndex + 1) % activeContacts.length],
		activeContacts[(startIndex + 2) % activeContacts.length],
	].filter(Boolean);

	return (
		<Fade in={!loading} timeout={600}>
			<Container maxWidth="md" sx={{ py: 3 }}>
				{/* Roster-today urgent banner */}
				{rosterToday && (
					<Button
						variant="contained"
						component={Link}
						to="/rosterToday"
						color="success"
						fullWidth
						size="large"
						sx={{
							mb: 2,
							py: 1.5,
							fontWeight: "bold",
							fontSize: "1rem",
						}}
					>
						Du har vakt i dag — klikk her
					</Button>
				)}

				{/* Opening hours hero card */}
				<Paper
					elevation={3}
					sx={{
						borderRadius: 2,
						overflow: "hidden",
						mb: 3,
					}}
				>
					<Box
						sx={{
							background:
								"linear-gradient(135deg, #e0e0e0 0%, #f5f5f5 50%, #e0e0e0 100%)",
							color: "black",
							px: 2.5,
							py: 1.2,
							display: "flex",
							alignItems: "center",
							gap: 1,
						}}
					>
						<Typography
							variant="h6"
							fontWeight="bold"
							sx={{ flexGrow: 1 }}
						>
							Åpningstider
						</Typography>
						{isSignedIn && (
							<IconButton
								component={Link}
								to="/calendar"
								size="small"
								sx={{ color: "black" }}
								aria-label="Åpne vaktkalender"
							>
								<CalendarMonthIcon />
							</IconButton>
						)}
						{isSignedIn && (
							<IconButton
								size="small"
								sx={{ color: "black" }}
								aria-label="Info om åpningstider"
								onClick={(e) => setInfoAnchor(e.currentTarget)}
							>
								<InfoOutlinedIcon fontSize="small" />
							</IconButton>
						)}
						<Popover
							open={Boolean(infoAnchor)}
							anchorEl={infoAnchor}
							onClose={() => setInfoAnchor(null)}
							anchorOrigin={{
								vertical: "bottom",
								horizontal: "right",
							}}
							transformOrigin={{
								vertical: "top",
								horizontal: "right",
							}}
						>
							<Typography
								variant="body2"
								sx={{ p: 2, maxWidth: 280 }}
							>
								Åpningstidene til Bak-Olsen er til enhver tid
								automatisk oppdatert basert på tilgjengelige
								vakter registert i kalenderen.
							</Typography>
						</Popover>
					</Box>
					<Box sx={{ px: 0, py: 0 }}>
						<PublicCalendar />
					</Box>
				</Paper>

				{/* Quick-action cards for signed-in users */}
				{isSignedIn && (
					<Stack
						direction={{ xs: "column", sm: "row" }}
						spacing={2}
						sx={{ mb: 3 }}
					>
						<Card
							variant="outlined"
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								borderRadius: 2,
								borderColor: "#e0e0e0",
								transition: "box-shadow 0.2s",
								"&:hover": { boxShadow: 4 },
							}}
						>
							<CardContent sx={{ pb: 0, flexGrow: 1 }}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									sx={{ mb: 1 }}
								>
									<CalendarMonthIcon color="primary" />
									<Typography variant="h6" fontWeight="bold">
										Vaktkalender
									</Typography>
								</Stack>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Registrer og hold oversikt over dine vakter.
									Vi ønsker dekning de neste 10 dagene — ta
									gjerne første ledige vakt.
								</Typography>
							</CardContent>
							<CardActions sx={{ px: 2, pb: 2 }}>
								<Button
									variant="contained"
									component={Link}
									to="/calendar"
									size="small"
									endIcon={<CalendarMonthIcon />}
								>
									Vaktkalender
								</Button>
							</CardActions>
						</Card>

						<Card
							variant="outlined"
							sx={{
								flex: 1,
								display: "flex",
								flexDirection: "column",
								borderRadius: 2,
								borderColor: "#e0e0e0",
								transition: "box-shadow 0.2s",
								"&:hover": { boxShadow: 4 },
							}}
						>
							<CardContent sx={{ pb: 0, flexGrow: 1 }}>
								<Stack
									direction="row"
									alignItems="center"
									spacing={1}
									sx={{ mb: 1 }}
								>
									<SchoolIcon color="primary" />
									<Typography variant="h6" fontWeight="bold">
										Utlånskalender
									</Typography>
								</Stack>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Se oversikt over reservasjoner og utlån for
									skoler.
								</Typography>
							</CardContent>
							<CardActions sx={{ px: 2, pb: 2 }}>
								<Button
									variant="contained"
									component={Link}
									to="/utlaanskalender"
									size="small"
									endIcon={<EventNoteIcon />}
								>
									Utlånskalender
								</Button>
							</CardActions>
						</Card>
					</Stack>
				)}

				{/* Season ticket CTA */}
				{(!profile?.hasOwnProperty("mandatoryRosters") ||
					profile.mandatoryRosters === 0) && (
					<Card
						variant="outlined"
						sx={{
							mb: 2,
							borderRadius: 2,
							background:
								"linear-gradient(135deg, #e8f5e9 0%, #f1f8e9 100%)",
							borderColor: "#a5d6a7",
						}}
					>
						<CardContent
							sx={{
								display: "flex",
								alignItems: "center",
								justifyContent: "space-between",
								flexWrap: "wrap",
								gap: 2,
								py: 2,
								"&:last-child": { pb: 2 },
							}}
						>
							<Box>
								<Typography variant="h6" fontWeight="bold">
									Kjøp sesongkort
								</Typography>
								<Typography
									variant="body2"
									color="text.secondary"
								>
									Sikre deg sesongkort for sesongen.
								</Typography>
							</Box>
							<Button
								variant="contained"
								color="success"
								component={Link}
								to="/store?showTypes=seasonTicket"
								endIcon={<ShoppingCartIcon />}
							>
								Gå til butikk
							</Button>
						</CardContent>
					</Card>
				)}

				{/* Collapsible secondary sections */}
				<Stack spacing={1.5}>
					<Accordion
						defaultExpanded={!profile?.secDocRead}
						disableGutters
						sx={{
							borderRadius: "8px !important",
							overflow: "hidden",
							"&:before": { display: "none" },
						}}
						variant="outlined"
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Stack
								direction="row"
								alignItems="center"
								spacing={1}
							>
								<MenuBookIcon color="action" />
								<Typography fontWeight="bold">
									Vaktopplæring
								</Typography>
								{!profile?.secDocRead && (
									<WarningAmberIcon
										color="warning"
										sx={{ fontSize: 20, ml: 1 }}
									/>
								)}
							</Stack>
						</AccordionSummary>
						<AccordionDetails>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 1.5 }}
							>
								For å sikre en trygg og effektiv drift av
								skiheisen er det påkrevd at alle vakter har satt
								seg inn i rutiner og prosedyrer for drift av
								anlegget.
							</Typography>
							<Button
								variant="outlined"
								size="small"
								onClick={() => setIsDocOpen(true)}
								sx={{ mb: 1.5 }}
							>
								Åpne vaktopplæring
							</Button>
							<Stack
								direction="row"
								alignItems="center"
								spacing={0.5}
							>
								<Checkbox
									checked={profile?.secDocRead ?? false}
									onChange={() =>
										updateProfile({
											secDocRead: !(
												profile?.secDocRead ?? false
											),
										})
									}
								/>
								<Typography variant="body2">
									Jeg har lest og forstått
									vaktopplæringsdokumentet.
								</Typography>
							</Stack>
						</AccordionDetails>
					</Accordion>

					<Accordion
						disableGutters
						sx={{
							borderRadius: "8px !important",
							overflow: "hidden",
							"&:before": { display: "none" },
						}}
						variant="outlined"
					>
						<AccordionSummary expandIcon={<ExpandMoreIcon />}>
							<Stack
								direction="row"
								alignItems="center"
								spacing={1}
							>
								<ContactPhoneIcon color="action" />
								<Typography fontWeight="bold">
									Teknisk feil / Spørsmål
								</Typography>
							</Stack>
						</AccordionSummary>
						<AccordionDetails>
							<Typography
								variant="body2"
								color="text.secondary"
								sx={{ mb: 1.5 }}
							>
								Hvis du opplever tekniske problemer eller har
								andre spørsmål som haster angående anlegget,
								kontakt vår tekniske gruppe. Dagens kontakter i
								prioritert rekkefølge:
							</Typography>
							<Stack
								direction={{ xs: "column", sm: "row" }}
								spacing={1}
								sx={{ mb: 1.5 }}
							>
								{contactsToShow.map((p) => (
									<Paper
										key={p.name + p.phoneNumber}
										variant="outlined"
										sx={{ px: 2, py: 1, borderRadius: 2 }}
									>
										<Typography
											variant="body2"
											fontWeight="bold"
										>
											{p.name}
										</Typography>
										<Typography
											variant="body2"
											color="text.secondary"
										>
											{p.phoneNumber}
										</Typography>
									</Paper>
								))}
							</Stack>
							<Divider sx={{ my: 1 }} />
							<Typography variant="body2" color="text.secondary">
								For andre spørsmål eller tilbakemeldinger, meld
								eller ring Vegar på 48011114.
							</Typography>
						</AccordionDetails>
					</Accordion>
				</Stack>

				{/* Vaktkalender info note for signed-in users */}
				<Modal
					onClose={() => {
						console.log("Closing popup");
						setIsDocOpen(false);
					}}
					open={isDocOpen}
				>
					<Box height="100%">
						<Button onClick={() => setIsDocOpen(false)}>
							Lukk
						</Button>
						<iframe
							title="learning"
							src="https://1drv.ms/b/c/ae115b60802cdd92/IQTzGoYf9JJvQr1qqw9kssuiAQxPDN3ltRUTqEC80K_tIcU"
							width="100%"
							height="100%"
						></iframe>
					</Box>
				</Modal>
			</Container>
		</Fade>
	);
}
